import React from "react";
import { useSiteContent } from "../content/SiteContentContext.jsx";
import { safeUrl } from "../utils/safeUrl";

const Contact = () => {
  const { contact, locations } = useSiteContent();
  const sedes = Array.isArray(locations) ? locations : [];

  // El sitio es estático (sin backend de contacto): el formulario abre el cliente de correo con los
  // datos ya cargados. Sin esto, el submit nativo recargaba la página y perdía lo escrito.
  const handleSubmit = (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const subject = encodeURIComponent(form.get("titulo") || "Contacto desde el sitio");
    const body = encodeURIComponent(
      `Nombre: ${form.get("nombre") || ""}\nCorreo: ${form.get("correo") || ""}\n\n${form.get("mensaje") || ""}`
    );
    const to = contact?.email || "contacto@incap.edu.mx";
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="w-full overflow-hidden footerAdjust">
      {/* ===== Hero ===== */}
      <section className="brand-gradient text-white relative overflow-hidden">
        <div className="blob-glow w-[420px] h-[420px] -top-40 -right-24" />
        <div className="mx-auto w-full max-w-[900px] px-6 py-16 md:py-20 text-center relative z-[1]">
          <span className="eyebrow on-dark justify-center">Contacto</span>
          <h1 className="mt-4 font-bold text-[34px] ss:text-[46px] leading-[1.1]">{contact?.title}</h1>
          {contact?.subtitle ? (
            <p className="mt-4 text-white/80 text-lg">{contact.subtitle}</p>
          ) : null}
        </div>
      </section>

      {/* ===== Planteles ===== */}
      <section className="brand-soft py-16 px-6">
        <div className="mx-auto w-full max-w-[1100px]">
          <div className="text-center max-w-[640px] mx-auto mb-10 fade-up">
            <span className="eyebrow justify-center">Nuestros planteles</span>
            <h2 className="mt-3 font-bold text-tPrimary text-[26px] ss:text-[32px]">
              Visítanos en cualquiera de nuestras sedes
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {sedes.map((sede, index) => (
              <div
                key={sede.id || index}
                className="card-modern fade-up p-7"
                style={{ animationDelay: `${index * 110}ms` }}
              >
                <div className="flex items-start gap-4">
                  <span className="icon-orb w-14 h-14 flex-shrink-0">
                    <i className="fa-solid fa-location-dot text-xl" />
                  </span>
                  <div className="flex-1 min-w-0 min-h-[134px]">
                    {sede.name ? (
                      <h3 className="font-bold text-tPrimary text-lg">{sede.name}</h3>
                    ) : null}
                    {sede.address ? (
                      <p className="text-tPrimary/80 mt-1.5 leading-relaxed">{sede.address}</p>
                    ) : null}
                    {sede.phone ? (
                      <p className="text-primary font-medium mt-2.5 flex items-center gap-2">
                        <i className="fa-solid fa-phone" /> {sede.phone}
                      </p>
                    ) : null}
                  </div>
                </div>

                {sede.mapEmbedUrl ? (
                  <div className="mt-5 rounded-xl overflow-hidden h-72 sm:h-80 lg:h-96">
                    <iframe
                      className="w-full h-full border-0"
                      src={safeUrl(sede.mapEmbedUrl)}
                      title={`Mapa ${sede.name || "plantel"}`}
                      loading="lazy"
                    ></iframe>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Formulario + información general ===== */}
      <section className="py-16 px-6">
        <div className="mx-auto w-full max-w-[1200px] grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="content-card lg:col-span-3 fade-up p-9 md:p-10">
            <div className="section-title mb-7">
              <span className="st-ico">
                <i className="fa-solid fa-paper-plane" />
              </span>
              {contact?.formTitle}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-5 mb-5 sm:grid-cols-2">
                <input
                  className="p-4 text-base border rounded-md outline-none"
                  type="text"
                  name="nombre"
                  placeholder="Nombre"
                />
                <input
                  className="p-4 text-base border rounded-md outline-none"
                  type="email"
                  name="correo"
                  placeholder="Correo electrónico"
                />
              </div>

              <input
                className="w-full p-4 text-base border rounded-md outline-none mb-5"
                type="text"
                name="titulo"
                placeholder="Título"
              />
              <textarea
                className="w-full p-4 text-base border rounded-md outline-none mb-6"
                rows="7"
                name="mensaje"
                placeholder="Déjanos tus dudas"
              ></textarea>

              <button type="submit" className="btn-brand w-full justify-center py-4 text-base">
                Enviar <i className="fa-solid fa-arrow-right" />
              </button>
            </form>
          </div>

          <div className="content-card lg:col-span-2 fade-up p-9 md:p-10" style={{ animationDelay: "110ms" }}>
            <div className="section-title mb-7">
              <span className="st-ico">
                <i className="fa-solid fa-address-card" />
              </span>
              Información
            </div>

            <div className="flex flex-col gap-5 text-tPrimary">
              {contact?.email ? (
                <p className="flex items-center gap-4 text-base">
                  <span className="icon-orb w-12 h-12 flex-shrink-0">
                    <i className="fa-solid fa-envelope" />
                  </span>
                  {contact.email}
                </p>
              ) : null}
              {contact?.phone ? (
                <p className="flex items-center gap-4 text-base">
                  <span className="icon-orb w-12 h-12 flex-shrink-0">
                    <i className="fa-solid fa-phone" />
                  </span>
                  {contact.phone}
                </p>
              ) : null}
            </div>

            {(contact?.facebookUrl || contact?.instagramUrl) ? (
              <>
                <h3 className="mt-9 mb-4 font-semibold text-tPrimary text-lg">Síguenos en nuestras redes</h3>
                <div className="flex gap-4">
                  {contact?.facebookUrl ? (
                    <a
                      href={safeUrl(contact.facebookUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="icon-orb w-14 h-14 text-lg"
                    >
                      <i className="fab fa-facebook-f" />
                    </a>
                  ) : null}
                  {contact?.instagramUrl ? (
                    <a
                      href={safeUrl(contact.instagramUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="icon-orb w-14 h-14 text-lg"
                    >
                      <i className="fab fa-instagram" />
                    </a>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
